import { useEffect, useMemo, useState } from "react";
import { useDrawer } from "@hooks/useDrawer";
import { useAuth } from "@Auth/useAuth";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { useStockStore } from "@stores/stockStore";
import { StockClient } from "@Clients/StockClient";
import { Stock } from "@models/Stock";
import { onInfoShow } from "@utils/onInfoShow";
import { onErrorShow } from "@utils/onErrorShow";
import { useProducts } from "@features/products/queries/useProducts";
import { useLocations } from "@features/locations/queries/useLocations";

/**
 * View-model for the transfer-stock drawer. The source placement is the stock
 * row identified by the drawer's `selectedId`; the user picks a destination and
 * a quantity (defaulting to the full amount). Moving everything reuses the
 * canonical full move/merge; a partial move decrements the source and merges or
 * creates the destination row — so totals stay correct either way.
 */
export function useTransferForm() {
  const { selectedId, close } = useDrawer();
  const { setTouch } = useConfirmationModalStore();
  const { baseUser } = useAuth();
  const { data: products = [] } = useProducts();
  const { data: locations = [] } = useLocations();
  const { stocks, getStocksByOrganizationId, moveStock, updateStock, createStock } =
    useStockStore();

  const [source, setSource] = useState<Stock | null>(null);
  const [destination, setDestination] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [errors, setErrors] = useState<{ destination?: string; quantity?: string }>(
    {}
  );
  const [submitting, setSubmitting] = useState(false);

  // Load the source row (store if warm, else fetch) and seed the full quantity.
  useEffect(() => {
    if (!selectedId) return;
    const seed = (stock: Stock) => {
      setSource(stock);
      setQuantity(stock.quantity);
    };
    const warm = stocks.find((s) => s.id === selectedId);
    if (warm) {
      seed(warm);
      return;
    }
    let active = true;
    StockClient.getStockById(selectedId)
      .then((stock) => {
        if (active && stock) seed(stock);
      })
      .catch(() => {
        /* surfaced elsewhere */
      });
    return () => {
      active = false;
    };
  }, [selectedId, stocks]);

  const product = useMemo(
    () => products.find((p) => p.id === source?.product_id) ?? null,
    [products, source]
  );
  const sourceLocation = useMemo(
    () => locations.find((l) => l.id === source?.location_id) ?? null,
    [locations, source]
  );
  const destinationOptions = useMemo(
    () => locations.filter((l) => l.id !== source?.location_id),
    [locations, source]
  );
  const selectedDestination = useMemo(
    () => locations.find((l) => l.id === destination) ?? null,
    [locations, destination]
  );

  const locationPath = (locationId: string): string => {
    const loc = locations.find((l) => l.id === locationId);
    return loc?.path?.length
      ? loc.path.map((p) => p.name).join(" / ")
      : loc?.name ?? "";
  };

  const setDest = (id: string) => {
    setDestination(id);
    setTouch(true);
  };
  const setQty = (n: number) => {
    setQuantity(n);
    setTouch(true);
  };

  const maxQuantity = source?.quantity ?? 0;

  const validate = (): boolean => {
    const next: { destination?: string; quantity?: string } = {};
    if (!destination) next.destination = "Destination is required";
    if (!quantity || quantity < 1) next.quantity = "Enter a quantity to move";
    else if (quantity > maxQuantity) next.quantity = `Only ${maxQuantity} available`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!source || !validate()) return;
    setSubmitting(true);
    try {
      if (quantity >= source.quantity) {
        // Whole row — repoint/merge and drop the source.
        await moveStock(source, destination);
      } else {
        // Partial — take from the source, then merge into or create the dest.
        await updateStock({ ...source, quantity: source.quantity - quantity });
        const existing = await StockClient.getStockByProductAndLocation(
          source.product_id,
          destination
        );
        if (existing) {
          await updateStock({
            ...existing,
            quantity: existing.quantity + quantity,
          });
        } else {
          await createStock({
            product_id: source.product_id,
            location_id: destination,
            quantity,
            organization_id: source.organization_id,
            current_status_id: source.current_status_id,
            committed_quantity: 0,
          });
        }
      }
      close();
      onInfoShow("Stock moved");
      getStocksByOrganizationId(baseUser!.organization_id!);
    } catch {
      onErrorShow("Failed to move stock");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    isLoading: !source,
    product,
    sourceLocation,
    destination,
    selectedDestination,
    destinationOptions,
    quantity,
    maxQuantity,
    errors,
    submitting,
    setDest,
    setQty,
    locationPath,
    submit,
  };
}
